import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { squareCropToCanvas, canvasToFile } from '../utils/image';
import { presignAvatar, uploadLocal, attachAvatar, removeAvatar } from '../lib/api/avatar';
import { getInitials } from '../utils/initials';
import { Button } from './ui/button';

const MAX_AVATAR_MB = 3;
const MAX_SIZE_BYTES = MAX_AVATAR_MB * 1024 * 1024;

const AvatarUploader = () => {
  const { user, token, refreshUser } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const processAndUpload = async (file) => {
    try {
      setUploading(true);
      setProgress(10);

      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        throw new Error('Only JPEG, PNG, and WebP images are supported');
      }

      // Validate file size
      if (file.size > MAX_SIZE_BYTES) {
        throw new Error(`File size must be less than ${MAX_AVATAR_MB}MB`);
      }

      setProgress(20);

      // Create FULL variant (512x512)
      const fullCanvas = await squareCropToCanvas(file, 512);
      setProgress(30);

      // Create THUMB variant (128x128)
      const thumbCanvas = await squareCropToCanvas(file, 128);
      setProgress(40);

      // Convert to files (WebP with EXIF stripped)
      const fullFile = await canvasToFile(fullCanvas, 'image/webp', 0.85);
      const thumbFile = await canvasToFile(thumbCanvas, 'image/webp', 0.8);
      setProgress(50);

      // Get presigned URLs for FULL variant
      const fullPresign = await presignAvatar('avatar-full.webp', 'image/webp', token);
      setProgress(60);

      // Get presigned URLs for THUMB variant
      const thumbPresign = await presignAvatar('avatar-thumb.webp', 'image/webp', token);
      setProgress(70);

      // Upload FULL
      let fullUrl;
      if (fullPresign.uploadUrl === '/api/media/upload-local') {
        const result = await uploadLocal(fullFile, `${fullPresign.key}-full.webp`, token);
        fullUrl = result.publicUrl;
      } else {
        // S3 upload
        await fetch(fullPresign.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/webp' },
          body: fullFile
        });
        fullUrl = fullPresign.publicUrl;
      }
      setProgress(80);

      // Upload THUMB
      let thumbUrl;
      if (thumbPresign.uploadUrl === '/api/media/upload-local') {
        const result = await uploadLocal(thumbFile, `${thumbPresign.key}-thumb.webp`, token);
        thumbUrl = result.publicUrl;
      } else {
        // S3 upload
        await fetch(thumbPresign.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/webp' },
          body: thumbFile
        });
        thumbUrl = thumbPresign.publicUrl;
      }
      setProgress(90);

      // Attach avatar to user profile
      await attachAvatar({
        key: fullPresign.key,
        url: fullUrl,
        thumbUrl: thumbUrl,
        width: 512,
        height: 512
      }, token);

      setProgress(100);

      // Refresh user data
      await refreshUser();

      toast.success('Avatar uploaded successfully!');
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error(error.message || 'Failed to upload avatar');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processAndUpload(acceptedFiles[0]);
    }
  }, [token]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    multiple: false,
    disabled: uploading
  });

  const handleRemove = async () => {
    if (!window.confirm('Are you sure you want to remove your avatar?')) {
      return;
    }

    try {
      await removeAvatar(token);
      await refreshUser();
      toast.success('Avatar removed successfully');
    } catch (error) {
      console.error('Avatar remove error:', error);
      toast.error(error.message || 'Failed to remove avatar');
    }
  };

  return (
    <div className="space-y-4">
      {/* Avatar Preview */}
      <div className="flex items-center justify-center">
        {user?.avatar?.thumbUrl ? (
          <img
            src={user.avatar.thumbUrl}
            alt={user.name}
            className="h-24 w-24 rounded-full ring-1 ring-brand-gold/40 bg-brand-surface object-cover"
            data-testid="header-avatar-preview"
          />
        ) : (
          <div
            className="h-24 w-24 rounded-full ring-1 ring-brand-gold/40 bg-brand-surface flex items-center justify-center text-2xl text-brand-gold font-medium"
            data-testid="header-avatar-initials"
          >
            {getInitials(user?.name || user?.email)}
          </div>
        )}
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive
            ? 'border-brand-gold bg-brand-gold/5'
            : 'border-zinc-700 hover:border-brand-gold/50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        data-testid="avatar-upload"
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="space-y-2">
            <p className="text-brand-text">Uploading... {progress}%</p>
            <div className="w-full bg-zinc-800 rounded-full h-2">
              <div
                className="bg-brand-gold h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        ) : isDragActive ? (
          <p className="text-brand-text">Drop your avatar here...</p>
        ) : (
          <div className="space-y-2">
            <p className="text-brand-text">
              Drag & drop your avatar, or click to select
            </p>
            <p className="text-sm text-brand-muted">
              JPG, PNG, or WebP (max {MAX_AVATAR_MB}MB)
            </p>
            <p className="text-xs text-brand-muted">
              Image will be cropped to square
            </p>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={() => document.querySelector('[data-testid="avatar-upload"] input').click()}
          disabled={uploading}
          className="flex-1 bg-brand-gold hover:bg-brand-gold/90 text-black"
        >
          Upload Avatar
        </Button>
        {user?.avatar && (
          <Button
            onClick={handleRemove}
            variant="outline"
            className="flex-1"
            data-testid="remove-avatar-button"
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default AvatarUploader;
